import React, { useState } from 'react';
import { parseQRCode, isEncryptedQRCode } from '../../utils/qrEncryption';

const ManualQRInput = ({ onScanResult, onError }) => {
  const [inputValue, setInputValue] = useState('');
  const [isValidJson, setIsValidJson] = useState(true);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    // Check if it's valid QR code format (either JSON or encrypted)
    if (value.trim()) {
      try {
        parseQRCode(value); // This will handle both encrypted and JSON formats
        setIsValidJson(true);
      } catch (err) {
        setIsValidJson(false);
      }
    } else {
      setIsValidJson(true);
    }
  };

  const handleSubmit = () => {
    if (!inputValue.trim()) {
      if (onError) {
        onError(new Error('Please enter QR code data'));
      }
      return;
    }

    try {
      // Parse using the universal parser (handles both encrypted and JSON)
      const parsedData = parseQRCode(inputValue);
      
      // Basic validation for required fields
      // worship_id is optional - can be missing, empty, or 0
      // Mobile requirement is validated in parseQRCode based on worship_id type

      console.log('✅ ManualQRInput: Valid QR data submitted:', parsedData);
      onScanResult(inputValue);
      setInputValue(''); // Clear input after successful submission
      
    } catch (err) {
      if (onError) {
        onError(new Error('Invalid JSON format. Please check your input.'));
      }
    }
  };

  const handleSampleData = () => {
    const sampleData = {
      worship_id: 123456,
      current_datetime: new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '').slice(0, 12)
    };
    setInputValue(JSON.stringify(sampleData, null, 2));
    setIsValidJson(true);
  };

  return (
    <div className="manual-qr-input">
      <div className="text-center mb-4">
        <p className="text-gray-600 mb-2">
          Manual QR Code Data Entry
        </p>
        <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
          <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
          Manual Input Mode
        </div>
      </div>

      <div className="mx-auto" style={{ maxWidth: '500px' }}>
        <div className="mb-4">
          <label htmlFor="qr-input" className="block text-sm font-medium text-gray-700 mb-2">
            Enter QR Code Data (JSON or Encrypted Format)
          </label>
          <textarea
            id="qr-input"
            rows="6"
            value={inputValue}
            onChange={handleInputChange}
            placeholder='{"worship_id": 123456, "current_datetime": "202508171400"} or encrypted string'
            className={`w-full px-3 py-2 border rounded-lg font-mono text-sm ${
              isValidJson ? 'border-gray-300' : 'border-red-300 bg-red-50'
            }`}
          />
          {!isValidJson && (
            <p className="text-red-500 text-sm mt-1">Invalid QR code format (must be JSON or encrypted string)</p>
          )}
        </div>

        <div className="flex space-x-2 justify-center">
          <button
            onClick={handleSampleData}
            className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded"
          >
            Use Sample Data
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValidJson || !inputValue.trim()}
            className={`font-bold py-2 px-4 rounded ${
              isValidJson && inputValue.trim()
                ? 'bg-green-500 hover:bg-green-600 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Submit QR Data
          </button>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 text-center text-sm text-gray-500">
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="font-semibold mb-2">Expected JSON Format:</p>
          <pre className="text-xs bg-white rounded border p-2 text-left">
{`{
  "mobile": "1234567890",
  "worship_id": 123456,
  "current_datetime": "202508171400"
}`}
          </pre>
          <div className="mt-2 text-xs">
            <p><strong>mobile:</strong> Required for normal members (non-4-digit worship_id); optional for 4-digit minor member worship_id</p>
            <p><strong>worship_id:</strong> Minor members can use 4-digit worship_id (mobile optional)</p>
            <p><strong>current_datetime:</strong> Date and time in YYYYMMDDHHMM format</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualQRInput;
